import { cn } from "@custodia/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Custodia brand mark + wordmark.
 *
 * Identity: a PROTECTIVE SHIELD embracing a heart — family care and protection,
 * never cold surveillance. The shield communicates security; the inner heart
 * keeps it warm and human.
 *
 * THEME-SAFE BY CONSTRUCTION (gotcha confirmed in sibling clones): drawing
 * brand elements with `text-accent` directly on the page background makes them
 * vanish under pale custom themes. Instead, the whole brand mark is drawn with
 * `currentColor` inside a SOLID `bg-accent` badge whose text color is
 * `text-accent-foreground` — a pair GUARANTEED to contrast by the theme engine.
 * The wordmark uses `text-foreground` (always readable against background),
 * so it never depends on the accent tone for readability.
 */
function BrandMark(props: { px: number }) {
  const glyph = () => Math.round(props.px * 0.64);
  return (
    <div
      class="grid place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
      style={{ width: `${props.px}px`, height: `${props.px}px` }}
      aria-hidden="true"
    >
      {/* Protective shield with a heart, all in currentColor (inherited from
       * text-accent-foreground) for guaranteed contrast.*/}
      <svg
        width={glyph()}
        height={glyph()}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="shrink-0"
      >
        {/* Shield body*/}
        <path d="M12 3.2l6.5 2.3v5.2c0 4.3-2.8 7.6-6.5 9.1-3.7-1.5-6.5-4.8-6.5-9.1V5.5L12 3.2Z" />
        {/* Warm heart inside the shield (filled with currentColor)*/}
        <path
          d="M12 15.4c-1.9-1.3-3.3-2.5-3.3-4a1.85 1.85 0 0 1 3.3-1.15A1.85 1.85 0 0 1 15.3 11.4c0 1.5-1.4 2.7-3.3 4Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
    </div>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <BrandMark px={32} />
          <span class="text-xl font-extrabold tracking-tight text-foreground">
            Custod<span class="font-semibold text-accent">ia</span>
          </span>
        </>
      ) : (
        <>
          <div class="mb-2 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BrandMark px={64} />
          </div>
          <h1 class="text-3xl font-extrabold tracking-tight text-foreground">
            Custod<span class="text-accent">ia</span>
          </h1>
        </>
      )}
    </div>
  );
}
